import type { EntityFilter } from "@pentacode/core";
import type { AnyRouter } from "@tanstack/router-core";
import { type TemplateResult, html } from "lit";
import { canAccess } from "../../../lib/route-tree";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { type AppRootRoute, type AppRouterContext, redirectTo } from "../../app-router-context";
import { type EmployeesAllPage, employeesAllPages, employeesAllRoute } from "../../app-routes";
import { routeAccessOf } from "../../route-access";
import { createBalancesRoutes } from "./balances.routes";
import { createCalendarRoutes } from "./calendar.routes";
import { createLedgersRoutes } from "./ledgers.routes";
import { createRecordsRoutes } from "./records.routes";
import { createTimeRoute } from "./time.route";

const BASE = "/employees/all";

function isPage(page: string): page is EmployeesAllPage {
    return (employeesAllPages as string[]).includes(page);
}

function access({ app, embedded }: AppRouterContext) {
    return routeAccessOf(app, embedded);
}

function firstAccessiblePage(context: AppRouterContext) {
    return ["core-data" as const, ...employeesAllPages].find((page) =>
        canAccess(employeesAllRoute(page), access(context))
    );
}

/** The child page ids under `employees/all` that have menu entries, read from the route tree itself. */
export function employeesAllMenuPages(router: AnyRouter) {
    return (
        router.routesById[`${BASE}` as never] as {
            children?: { path: string; options: { staticData?: { menu?: unknown } } }[];
        }
    )
        .children!.filter((child) => child.options.staticData?.menu)
        .map((child) => child.path.split("/")[0] as EmployeesAllPage)
        .filter((page, i, pages) => pages.indexOf(page) === i)
        .sort((a, b) => employeesAllPages.indexOf(a) - employeesAllPages.indexOf(b));
}

export function createEmployeesAllLayoutRoute(parent: AppRootRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "employees/all",
        beforeLoad: ({ location, context }): void => {
            const page = location.pathname.slice(BASE.length + 1).split("/")[0];

            if (page === "ledgers") {
                redirectTo(`${BASE}/ledgers-month`);
            }

            if (!isPage(page) || !canAccess(employeesAllRoute(page), access(context))) {
                if (page) {
                    context.reportDenied(location.pathname.slice(1));
                }
                const fallback = firstAccessiblePage(context);
                redirectTo(fallback ? `${BASE}/${fallback}` : "/");
            }

            const entityFilters = (location.search as Record<string, string | undefined>)["entity-filters"];
            if (entityFilters) {
                try {
                    const filters = JSON.parse(entityFilters) as EntityFilter[];
                    if (Array.isArray(filters) && filters.length) {
                        context.app.updateEmployeeFilters({ filters });
                    }
                } catch (e) {
                    console.error("Error parsing entity-filters:", e);
                }
            }
        },
        component: ({ match, router }): TemplateResult => {
            if ((router.options.context as { mode: string }).mode === "view") {
                return html`<ptc-route-outlet></ptc-route-outlet>`;
            }
            const matches = router.stores.matches.get();
            const child = matches[matches.findIndex((m) => m.id === match.id) + 1];
            return html`<ptc-employees-all
                .page=${child?.pathname.slice(BASE.length + 1).split("/")[0] ?? ""}
                .search=${router.stores.location.get().search}
                .pages=${employeesAllMenuPages(router)}
                ?loading=${router.stores.__store.get().isLoading}
                ><slot name="submenu" slot="submenu"></slot
            ></ptc-employees-all>`;
        },
    }).lazy(lazyElement(() => import("./index")));
}

export type EmployeesAllLayoutRoute = ReturnType<typeof createEmployeesAllLayoutRoute>;

export function createEmployeesAllRoute(parent: AppRootRoute) {
    const layout = createEmployeesAllLayoutRoute(parent);
    // Unknown pages must still match below the layout, so its beforeLoad redirects them.
    const unknownPage = createRoute({ getParentRoute: () => layout, path: "$" });
    return layout.addChildren([
        createTimeRoute(layout),
        ...createBalancesRoutes(layout),
        ...createLedgersRoutes(layout),
        ...createCalendarRoutes(layout),
        ...createRecordsRoutes(layout),
        unknownPage,
    ]);
}
